import React, { useState, useEffect, useRef } from 'react';

// --- HELPER & UTILITY COMPONENTS (These would typically be in a shared components.jsx file) ---
import Header from "./Header"; // ✅ Added Header import
import Footer from "./Footer"; // ✅ Added Footer import

const useOnScreen = (options) => {
    const ref = useRef();
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const currentRef = ref.current;
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setIsVisible(true);
                if (currentRef) {
                    observer.unobserve(currentRef);
                }
            }
        }, options);

        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [ref, options]);
    
    return [ref, isVisible];
};

const AnimatedSection = ({ children, className, id }) => {
    const [ref, isVisible] = useOnScreen({ threshold: 0.1 });
    return (
        <section ref={ref} id={id} className={`${className} transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            {children}
        </section>
    );
};

const PageHeader = ({ title, subtitle }) => (
    <section className="relative py-24 bg-slate-50">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50 to-slate-50 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
            <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight">{title}</h1>
            <p className="mt-4 max-w-2xl mx-auto text-lg text-slate-600">{subtitle}</p>
        </div>
    </section>
);

// --- ICONS & COMPONENTS FOR THIS PAGE ---
const Copy = ({ className }) => (
    <svg xmlns="http://www.w.3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
);
const Check = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><polyline points="20 6 9 17 4 12"></polyline></svg>
);

const CodeBlock = ({ codeString, language = 'bash' }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(codeString);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="bg-slate-800 rounded-lg overflow-hidden my-4 relative">
            <div className="absolute top-2 right-2">
                <button onClick={handleCopy} className="text-slate-400 hover:text-white transition-colors p-2 rounded-md bg-slate-700/50 hover:bg-slate-600/50">
                    {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                </button>
            </div>
            <pre className={`language-${language} p-4 text-sm overflow-x-auto`}>
                <code className="text-white">{codeString}</code>
            </pre>
        </div>
    );
};

// --- MAIN PAGE COMPONENT ---
const ApiDocsPage = () => {
    // A simple mock for scrolling to sections
    const scrollTo = (id) => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    };
    
    return (
        <>
            <Header /> {/* ✅ Added Header here */}

            <main>
                <PageHeader
                    title="API Documentation"
                    subtitle="Integrate Outreach's power into your own applications and workflows."
                />

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                    <div className="flex flex-col lg:flex-row gap-8">
                        {/* Sidebar Navigation */}
                        <aside className="lg:w-1/4 lg:sticky top-24 self-start">
                            <nav className="space-y-4">
                                <h3 className="font-bold text-lg text-slate-800">Getting Started</h3>
                                <ul className="space-y-2 border-l-2 border-slate-200 pl-4">
                                    <li><button onClick={() => scrollTo('authentication')} className="text-slate-600 hover:text-blue-600">Authentication</button></li>
                                </ul>

                                <h3 className="font-bold text-lg text-slate-800 pt-4">Endpoints</h3>
                                 <ul className="space-y-2 border-l-2 border-slate-200 pl-4">
                                    <li><button onClick={() => scrollTo('leads-api')} className="text-slate-600 hover:text-blue-600">Leads API</button></li>
                                    <li><button onClick={() => scrollTo('campaigns-api')} className="text-slate-600 hover:text-blue-600">Campaigns API</button></li>
                                </ul>

                                 <h3 className="font-bold text-lg text-slate-800 pt-4">General</h3>
                                 <ul className="space-y-2 border-l-2 border-slate-200 pl-4">
                                    <li><button onClick={() => scrollTo('error-codes')} className="text-slate-600 hover:text-blue-600">Error Codes</button></li>
                                </ul>
                            </nav>
                        </aside>

                        {/* Main Content */}
                        <div className="lg:w-3/4 space-y-16">
                            <AnimatedSection id="authentication">
                                <h2 className="text-3xl font-extrabold text-slate-900 border-b pb-4">Authentication</h2>
                                <p className="mt-4 text-slate-600">
                                    All API requests require authentication. You can generate an API key from your account settings. Include this key in the <code className="bg-slate-200 text-sm p-1 rounded">Authorization</code> header of your requests as a Bearer token.
                                </p>
                                {/* <CodeBlock 
                                    codeString={`curl "https://api.roboman.ai/v1/leads" \\\n  -H "Authorization: Bearer YOUR_API_KEY"`} 
                                /> */}
                            </AnimatedSection>

                            <AnimatedSection id="leads-api">
                                 <h2 className="text-3xl font-extrabold text-slate-900 border-b pb-4">Leads API</h2>
                                 <p className="mt-4 text-slate-600">The Leads API allows you to create, retrieve, and manage your contacts.</p>
                                 <div className="mt-8 p-6 border rounded-lg">
                                    <div className="flex items-center gap-4">
                                        <span className="text-sm font-bold bg-green-100 text-green-800 px-3 py-1 rounded-full">GET</span>
                                        <code className="text-slate-800 font-mono">/v1/leads/&#123;leadId&#125;</code>
                                    </div>
                                    <p className="mt-2 text-slate-600">Retrieves a specific lead by their ID.</p>
                                    <h4 className="font-bold mt-4">Response Example:</h4>
                                    <CodeBlock 
                                        language="json"
                                        codeString={JSON.stringify({
                                            id: "lead_12345",
                                            email: "john.doe@example.com",
                                            firstName: "John",
                                            lastName: "Doe",
                                            company: "Example Inc.",
                                            createdAt: "2025-09-15T10:30:00Z"
                                        }, null, 2)}
                                    />
                                 </div>
                            </AnimatedSection>

                            <AnimatedSection id="campaigns-api">
                                 <h2 className="text-3xl font-extrabold text-slate-900 border-b pb-4">Campaigns API</h2>
                                 <p className="mt-4 text-slate-600">Manage your outreach campaigns programmatically.</p>
                                 <div className="mt-8 p-6 border rounded-lg">
                                    <div className="flex items-center gap-4">
                                        <span className="text-sm font-bold bg-blue-100 text-blue-800 px-3 py-1 rounded-full">POST</span>
                                        <code className="text-slate-800 font-mono">/v1/campaigns/&#123;campaignId&#125;/add_lead</code>
                                    </div>
                                    <p className="mt-2 text-slate-600">Adds a lead to a specific campaign.</p>
                                    <h4 className="font-bold mt-4">Request Body Example:</h4>
                                    <CodeBlock 
                                        language="json"
                                        codeString={JSON.stringify({ leadId: "lead_12345" }, null, 2)}
                                    />
                                 </div>
                            </AnimatedSection>
                            
                            <AnimatedSection id="error-codes">
                                 <h2 className="text-3xl font-extrabold text-slate-900 border-b pb-4">Error Codes</h2>
                                <p className="mt-4 text-slate-600">
                                    Our API uses conventional HTTP status codes to indicate the success or failure of a request.
                                </p>
                                <div className="mt-4 overflow-x-auto">
                                    <table className="min-w-full divide-y divide-slate-200">
                                        <thead className="bg-slate-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Code</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Description</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-slate-200">
                                            <tr><td className="px-6 py-4 whitespace-nowrap"><code className="bg-slate-200 text-sm p-1 rounded">400</code></td><td className="px-6 py-4">Bad Request — Your request is malformed.</td></tr>
                                            <tr><td className="px-6 py-4 whitespace-nowrap"><code className="bg-slate-200 text-sm p-1 rounded">401</code></td><td className="px-6 py-4">Unauthorized — Your API key is wrong.</td></tr>
                                            <tr><td className="px-6 py-4 whitespace-nowrap"><code className="bg-slate-200 text-sm p-1 rounded">404</code></td><td className="px-6 py-4">Not Found — The requested resource doesn't exist.</td></tr>
                                            <tr><td className="px-6 py-4 whitespace-nowrap"><code className="bg-slate-200 text-sm p-1 rounded">500</code></td><td className="px-6 py-4">Server Error — Something went wrong on our end.</td></tr>
                                        </tbody>
                                    </table>
                                </div>
                            </AnimatedSection>
                        </div>
                    </div>
                </div>
            </main>

            <Footer /> {/* ✅ Added Footer here */}
        </>
    );
};

export default ApiDocsPage;
